import dayjs, { type Dayjs } from "dayjs";
import type { SchedulePick, SchedulePickReason, ScheduleSlot } from "../../service/schedulePick.service";
import type { TClientForm } from "../../types/index.type";
import { scheduleSlotLabel } from "../../utils/clientSchedule";

/**
 * Where the drawer's current patient came from. Carried on every event so the
 * post-ship question — how often does the pick hold, and what replaces it when it
 * does not — is answerable from PostHog alone.
 */
export type PatientPickSource =
  | "autopick" // the drawer's own pre-selection, untouched
  | "recommended" // chosen from the Recommended group
  | "up_next" // chosen from the rest of today's sheet
  | "documented" // chosen from patients already documented today (a second note)
  | "browse" // scrolled the full roster below the schedule groups
  | "search" // typed and picked a search result
  | "new_patient" // the inline Add Patient form
  | "sidebar" // arrived pre-filled from the sidebar's selected patient
  | "cleared" // the clinician cleared the field
  | "none"; // nothing chosen

/** The pre-selection made for one drawer open, frozen so later events can describe it. */
export type AutopickRecord = {
  clientId: number;
  reason: SchedulePickReason;
  confidence: "high" | "low";
  tieSize: number;
  slotOffsetMin: number;
};

export type ScheduleGroupKey = "recommended" | "up_next" | "documented" | "all";

export type ScheduleRow = {
  client: TClientForm;
  /** The schedule row behind this option; null in the "all patients" group. */
  slot: ScheduleSlot | null;
  source: PatientPickSource;
  /** 1-based position inside its group (the pick is recommended #1). */
  rank: number;
  picked: boolean;
};

export type ScheduleGroup = {
  key: ScheduleGroupKey;
  title: string;
  /** Right-aligned aside on the group header, e.g. "3 at 10:30 AM · 1 at 10:45 AM". */
  note: string;
  rows: ScheduleRow[];
};

export const minutesSince = (iso: string | null | undefined, now: Dayjs = dayjs()): number | null => {
  if (!iso) return null;
  const at = dayjs(iso);
  return at.isValid() ? now.diff(at, "minute") : null;
};

export const firstName = (name: string | null | undefined): string => (name || "").trim().split(/\s+/)[0] || "them";

/** "3 at 10:30 AM · 1 at 10:45 AM", or just "10:45 AM" when every slot is singly booked. */
export const describeSlots = (slots: ScheduleSlot[], zone: string): string => {
  const counts = new Map<string, number>();
  for (const s of slots) {
    const label = scheduleSlotLabel(s.scheduled_at, zone);
    if (label) counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const anyShared = Array.from(counts.values()).some((n) => n > 1);
  return Array.from(counts.entries())
    .map(([label, n]) => (anyShared ? `${n} at ${label}` : label))
    .join(" · ");
};

/**
 * The picker's browse list when today's sheet is known: the three schedule groups
 * from the server, then the rest of the roster (minus anyone already listed) so a
 * walk-in is still one scroll away. Empty groups are dropped.
 */
export const buildScheduleGroups = (
  pick: SchedulePick | null,
  browse: TClientForm[],
  opts: { zone: string; autopickId: number | null; allTitle: string; now?: Dayjs },
): ScheduleGroup[] => {
  const now = opts.now ?? dayjs();
  const groups: ScheduleGroup[] = [];
  const seen = new Set<number>();
  const rows = (slots: ScheduleSlot[], source: PatientPickSource): ScheduleRow[] =>
    slots.map((slot, i) => {
      if (slot.client.id != null) seen.add(slot.client.id);
      return { client: slot.client, slot, source, rank: i + 1, picked: slot.client.id === opts.autopickId };
    });

  if (pick && pick.state === "ready") {
    if (pick.recommended.length) {
      groups.push({
        key: "recommended",
        title: "Recommended",
        note: describeSlots(pick.recommended, opts.zone),
        rows: rows(pick.recommended, "recommended"),
      });
    }
    if (pick.up_next.length) {
      const allAhead = pick.up_next.every((s) => dayjs(s.scheduled_at).isAfter(now));
      groups.push({
        key: "up_next",
        title: allAhead ? "Up next" : "Remaining today",
        note: "",
        rows: rows(pick.up_next, "up_next"),
      });
    }
    if (pick.documented.length) {
      groups.push({
        key: "documented",
        title: "Documented today",
        note: `${pick.documented.length} of ${pick.schedule_size}`,
        rows: rows(pick.documented, "documented"),
      });
    }
  }

  const rest = browse.filter((c) => c.id == null || !seen.has(c.id));
  if (rest.length) {
    groups.push({
      key: "all",
      title: groups.length ? opts.allTitle : "",
      note: "",
      rows: rest.map((client, i) => ({ client, slot: null, source: "browse", rank: i + 1, picked: false })),
    });
  }
  return groups;
};

export const findSlot = (pick: SchedulePick | null, clientId: number | null | undefined): ScheduleSlot | null => {
  if (!pick || clientId == null) return null;
  for (const group of [pick.recommended, pick.up_next, pick.documented]) {
    const hit = group.find((s) => s.client.id === clientId);
    if (hit) return hit;
  }
  return null;
};

/** "synced 1:02 AM" today, "last synced Mon 1:02 AM" for an older stamp, "not synced yet" for none. */
export const syncedLabel = (pick: Pick<SchedulePick, "synced_at">, zone: string, now: Dayjs = dayjs()): string => {
  if (!pick.synced_at) return "not synced yet";
  const at = dayjs(pick.synced_at);
  if (!at.isValid()) return "not synced yet";
  const local = at.tz(zone);
  return local.isSame(now.tz(zone), "day")
    ? `synced ${local.format("h:mm A")}`
    : `last synced ${local.format("ddd h:mm A")}`;
};

export type SchedulePickBarModel = {
  tone: "indigo" | "neutral" | "warn";
  lead: string;
  detail: string[];
  action: { kind: "switch" | "sync" | "use_pick"; label: string } | null;
};

/**
 * What the bar under the patient field says. One place for the copy so every
 * state in the design (single pick, tie, running behind, first of day, no
 * schedule, sidebar choice) is a row here and a test case, not a branch in JSX.
 */
export const buildSchedulePickBar = (args: {
  pick: SchedulePick | null;
  autopick: AutopickRecord | null;
  selectedClientId: number | null | undefined;
  lastSource: PatientPickSource;
  zone: string;
  now?: Dayjs;
}): SchedulePickBarModel | null => {
  const { pick, autopick, selectedClientId, lastSource, zone } = args;
  if (!pick) return null;
  const now = args.now ?? dayjs();
  const time = (slot: ScheduleSlot | null) => (slot ? scheduleSlotLabel(slot.scheduled_at, zone) : "");
  const noteTime = (slot: ScheduleSlot | null) =>
    slot?.documented_at ? `note started ${scheduleSlotLabel(slot.documented_at, zone)}` : "";
  const suggestionSlot = findSlot(pick, pick.suggestion?.client_id);
  const selectedSlot = findSlot(pick, selectedClientId);

  // The pre-selection, still in place.
  if (autopick && selectedClientId === autopick.clientId && selectedSlot) {
    const t = time(selectedSlot);
    const switchAction = { kind: "switch" as const, label: `Not ${firstName(selectedSlot.client.name)}? Switch` };
    switch (autopick.reason) {
      case "in_window":
        return {
          tone: "indigo",
          lead: `Up now · ${t}`,
          detail: [
            "picked from today's schedule",
            autopick.tieSize > 1 ? `${autopick.tieSize - 1} more booked at ${t}` : "",
          ].filter(Boolean),
          action: switchAction,
        };
      case "next_after_last_note":
        return {
          tone: "indigo",
          lead: `Next after your last note · ${t}`,
          detail: ["nothing else booked around now"],
          action: switchAction,
        };
      case "next_undocumented":
        return {
          tone: "neutral",
          lead: `Next on your schedule · ${t}`,
          detail: ["nothing booked around now"],
          action: switchAction,
        };
      case "first_of_day":
        return {
          tone: "neutral",
          lead: `First up · ${t}`,
          detail: [`schedule ${syncedLabel(pick, zone, now)}`],
          action: switchAction,
        };
    }
  }

  // Arrived from the sidebar with a different patient than the schedule would pick.
  if (lastSource === "sidebar" && suggestionSlot && selectedClientId !== pick.suggestion?.client_id) {
    return {
      tone: "neutral",
      lead: "Using your sidebar selection",
      detail: [
        selectedSlot ? `booked ${time(selectedSlot)}` : "not on today's schedule",
        noteTime(selectedSlot),
      ].filter(Boolean),
      action: { kind: "use_pick", label: `Use schedule pick: ${firstName(suggestionSlot.client.name)}` },
    };
  }

  if (selectedClientId != null) {
    if (selectedSlot) {
      return {
        tone: "neutral",
        lead: `${time(selectedSlot)} · from today's schedule`,
        detail: [noteTime(selectedSlot)].filter(Boolean),
        action: { kind: "switch", label: "Switch" },
      };
    }
    if (pick.state === "ready") {
      return {
        tone: "neutral",
        lead: "Not on today's schedule",
        detail: [],
        action: { kind: "switch", label: "Switch" },
      };
    }
    return null;
  }

  // Nothing selected.
  if (pick.state !== "ready") {
    return {
      tone: "warn",
      lead: "No schedule for today",
      detail: [syncedLabel(pick, zone, now)],
      action: { kind: "sync", label: "Sync from EHR" },
    };
  }
  if (pick.no_pick_reason === "all_documented") {
    return {
      tone: "neutral",
      lead: "Everyone on today's schedule is documented",
      detail: [`${pick.documented.length} of ${pick.schedule_size}`],
      action: { kind: "switch", label: "Choose" },
    };
  }
  if (suggestionSlot) {
    return {
      tone: "neutral",
      lead: `Suggested: ${suggestionSlot.client.name} · ${time(suggestionSlot)}`,
      detail: [],
      action: { kind: "use_pick", label: "Use" },
    };
  }
  return null;
};
